import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClient, addInteraction, addFeedback, uploadClientPhoto, uploadClientPhotoBoutique } from '../services/api';
import Chart from 'react-apexcharts';
import {
    FiArrowLeft, FiActivity, FiDollarSign, FiUsers, FiTarget,
    FiTrendingUp, FiTrendingDown, FiMapPin, FiMail, FiPhone, FiCalendar,
    FiStar, FiAlertCircle, FiCheckCircle, FiEye, FiShoppingBag,
    FiClock, FiAward, FiBarChart2, FiList, FiPieChart, FiCamera,
    FiMessageSquare, FiDownload, FiPrinter, FiX, FiImage, FiHash,
    FiUser, FiGlobe, FiNavigation, FiPackage
} from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isDev ? 'http://localhost:5055' : '';

const MOIS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const TYPE_COLORS: Record<string, string> = {
    visite: '#3B82F6', vente: '#16A34A', commande: '#8B5CF6',
    livraison: '#F59E0B', prospection: '#14B8A6', installation: '#EF4444',
    reclamation: '#EC4899', recouvrement: '#6366F1', formation: '#06B6D4'
};
const TYPE_ICONS: Record<string, string> = {
    visite: '👁', vente: '💰', commande: '📦',
    livraison: '🚚', prospection: '🔍', installation: '🔧',
    reclamation: '⚠️', recouvrement: '💳', formation: '📚'
};

const EmptyState = ({ text }: { text: string }) => (
    <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94A3B8' }}>
        <FiAlertCircle size={28} style={{ marginBottom: 8, opacity: 0.5 }} />
        <p style={{ fontSize: 13, margin: 0 }}>{text}</p>
    </div>
);

const ClientDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'resume' | 'activites' | 'interactions' | 'feedbacks'>('resume');
    const [newInteraction, setNewInteraction] = useState({ type: 'note', contenu: '' });
    const [newFeedback, setNewFeedback] = useState({ note: 5, commentaire: '' });
    const [showQRModal, setShowQRModal] = useState(false);
    const qrRef = useRef<HTMLDivElement>(null);

    const n = (v: any) => Math.round(Number(v) || 0);
    const fmt = (v: any) => n(v).toLocaleString('fr-FR');

    const loadDetail = () => {
        if (!id) return;
        setLoading(true);
        getClient(parseInt(id))
            .then(res => setData(res.data))
            .catch(() => navigate('/admin/clients'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadDetail(); }, [id]);

    const handlePhotoUpload = async (file: File) => {
        if (!id) return;
        try {
            await uploadClientPhoto(parseInt(id), file);
            loadDetail();
        } catch { /* ignore */ }
    };

    const handlePhotoBoutiqueUpload = async (file: File) => {
        if (!id) return;
        try {
            await uploadClientPhotoBoutique(parseInt(id), file);
            loadDetail();
        } catch { /* ignore */ }
    };

    const handleAddInteraction = async () => {
        if (!newInteraction.contenu) return;
        try {
            await addInteraction(Number(id), newInteraction);
            setNewInteraction({ type: 'note', contenu: '' });
            loadDetail();
        } catch { /* ignore */ }
    };

    const handleAddFeedback = async () => {
        try {
            await addFeedback(Number(id), newFeedback);
            setNewFeedback({ note: 5, commentaire: '' });
            loadDetail();
        } catch { /* ignore */ }
    };

    const qrValue = `SP-CLIENT-${id}`;

    const downloadQR = () => {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return;
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        canvas.width = 400; canvas.height = 500;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const img = new Image();
        img.onload = () => {
            ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 400, 500);
            ctx.drawImage(img, 50, 30, 300, 300);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 20px sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(data?.raison_sociale || '', 200, 370);
            ctx.fillStyle = '#64748B'; ctx.font = '14px sans-serif';
            ctx.fillText(qrValue, 200, 395);
            ctx.fillText(`${data?.nom_contact || ''} - ${data?.telephone || ''}`, 200, 418);
            ctx.fillStyle = '#16A34A'; ctx.font = 'bold 12px sans-serif';
            ctx.fillText('SalesPulse', 200, 460);
            const link = document.createElement('a');
            link.download = `QR-${data?.raison_sociale || 'client'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    const printQR = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        printWindow.document.write(`
            <html><head><title>QR Code - ${data?.raison_sociale}</title>
            <style>
                body { font-family: -apple-system, sans-serif; text-align: center; padding: 40px; }
                .qr-print { display: inline-block; padding: 30px; border: 2px solid #E2E8F0; border-radius: 16px; }
                h2 { margin: 20px 0 4px; color: #1E293B; font-size: 22px; }
                .code { color: #64748B; font-size: 14px; margin-bottom: 6px; }
                .info { color: #94A3B8; font-size: 13px; }
                .brand { color: #16A34A; font-weight: 700; font-size: 12px; margin-top: 16px; }
                @media print { body { padding: 20px; } }
            </style></head><body>
            <div class="qr-print">
                ${qrRef.current?.innerHTML || ''}
                <h2>${data?.raison_sociale || ''}</h2>
                <div class="code">${qrValue}</div>
                <div class="info">${data?.nom_contact || ''} ${data?.prenom_contact || ''} - ${data?.telephone || ''}</div>
                <div class="brand">SalesPulse</div>
            </div>
            <script>window.onload = function() { window.print(); }</script>
            </body></html>
        `);
        printWindow.document.close();
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 500 }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ width: 40, height: 40, border: '3px solid #E2E8F0', borderTopColor: '#16A34A', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ color: '#94A3B8', fontSize: 14 }}>Chargement...</p>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
    if (!data) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 500, color: '#EF4444', fontSize: 15 }}>
            Client non trouve
        </div>
    );

    const sg = data.stats_global || {};
    const sm = data.stats_mois || {};
    const sp = data.stats_mois_precedent || {};
    const evolution = data.evolution || [];
    const activites = data.activites || [];
    const interactions = data.interactions || [];
    const feedbacks = data.feedbacks || [];
    const parType = data.par_type || [];
    const topCommerciaux = data.top_commerciaux || [];
    const noteMoyenne = data.note_moyenne || {};

    const growth = (curr: number, prev: number) => prev > 0 ? Math.round(((curr - prev) / prev) * 100) : (curr > 0 ? 100 : 0);
    const caGrowth = growth(n(sm.ca), n(sp.ca));
    const actGrowth = growth(n(sm.nb_activites), n(sp.nb_activites));

    const potentielColor = data.potentiel === 'eleve' ? '#16A34A' : data.potentiel === 'moyen' ? '#F59E0B' : '#94A3B8';

    return (
        <div>
            {/* =================== FACEBOOK-STYLE HERO HEADER =================== */}
            <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 22, background: '#fff', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                {/* Cover photo (photo_boutique) */}
                <div style={{
                    position: 'relative', height: 220,
                    background: data.photo_boutique
                        ? `url(${API_URL}${data.photo_boutique}) center/cover no-repeat`
                        : 'linear-gradient(135deg, #14532D 0%, #166534 50%, #1A7A42 100%)',
                }}>
                    {/* Dark overlay for text readability */}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%)' }} />

                    {/* Top bar: back + actions */}
                    <div style={{ position: 'absolute', top: 16, left: 16, right: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 }}>
                        <button onClick={() => navigate('/admin/clients')}
                            style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)', border: 'none', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', transition: 'background 0.2s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.3)'}>
                            <FiArrowLeft size={18} />
                        </button>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setShowQRModal(true)}
                                style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)', border: 'none', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 600 }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.3)'}>
                                <FiHash size={14} /> QR Code
                            </button>
                            <label style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)', border: 'none', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                                <FiCamera size={14} /> Photo boutique
                                <input type="file" accept="image/*" style={{ display: 'none' }}
                                    onChange={e => { if (e.target.files?.[0]) handlePhotoBoutiqueUpload(e.target.files[0]); }} />
                            </label>
                        </div>
                    </div>

                    {/* Status badges */}
                    <div style={{ position: 'absolute', bottom: 70, right: 20, display: 'flex', gap: 8, zIndex: 2 }}>
                        {data.jours_inactif <= 7 ? (
                            <span style={{ background: 'rgba(34,197,94,0.25)', backdropFilter: 'blur(8px)', color: '#86EFAC', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#4ADE80', marginRight: 6, verticalAlign: 'middle' }} />
                                Actif
                            </span>
                        ) : data.jours_inactif <= 30 ? (
                            <span style={{ background: 'rgba(251,191,36,0.25)', backdropFilter: 'blur(8px)', color: '#FDE68A', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                                <FiClock size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {data.jours_inactif}j inactif
                            </span>
                        ) : (
                            <span style={{ background: 'rgba(239,68,68,0.25)', backdropFilter: 'blur(8px)', color: '#FCA5A5', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                                <FiAlertCircle size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {data.jours_inactif}j inactif
                            </span>
                        )}
                        <span style={{ background: potentielColor + '30', backdropFilter: 'blur(8px)', color: '#fff', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: `1px solid ${potentielColor}50` }}>
                            <FiTarget size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            Potentiel {data.potentiel}
                        </span>
                    </div>
                </div>

                {/* Profile section below cover */}
                <div style={{ position: 'relative', padding: '0 28px 20px' }}>
                    {/* Client avatar overlapping cover */}
                    <div style={{ position: 'relative', marginTop: -50, display: 'flex', alignItems: 'flex-end', gap: 20 }}>
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                            {data.image ? (
                                <img src={`${API_URL}${data.image}`} alt=""
                                    style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '4px solid #fff', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }} />
                            ) : (
                                <div style={{
                                    width: 100, height: 100, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #14532D, #16A34A)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontSize: 32, fontWeight: 800, letterSpacing: -0.5,
                                    border: '4px solid #fff', boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
                                }}>
                                    {(data.raison_sociale || '??').substring(0, 2).toUpperCase()}
                                </div>
                            )}
                            <label style={{
                                position: 'absolute', bottom: 2, right: 2, width: 28, height: 28, borderRadius: '50%',
                                background: '#fff', border: '2px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                            }}>
                                <FiCamera size={12} color="#16A34A" />
                                <input type="file" accept="image/*" style={{ display: 'none' }}
                                    onChange={e => { if (e.target.files?.[0]) handlePhotoUpload(e.target.files[0]); }} />
                            </label>
                        </div>

                        <div style={{ flex: 1, minWidth: 0, paddingBottom: 4 }}>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1E293B', letterSpacing: -0.5, lineHeight: 1.2 }}>
                                {data.raison_sociale}
                            </h1>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: '#64748B', fontSize: 13, marginTop: 6 }}>
                                {data.nom_contact && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiUser size={13} /> {data.prenom_contact} {data.nom_contact}</span>}
                                {data.telephone && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiPhone size={13} /> {data.telephone}</span>}
                                {data.email && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiMail size={13} /> {data.email}</span>}
                                {data.ville && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><FiMapPin size={13} /> {data.ville}, {data.pays || 'Senegal'}</span>}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                            {data.segment_nom && (
                                <div style={{
                                    background: (data.segment_couleur || '#16A34A') + '12',
                                    border: `1px solid ${(data.segment_couleur || '#16A34A')}30`,
                                    borderRadius: 12, padding: '8px 16px', textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Segment</div>
                                    <div style={{ color: data.segment_couleur || '#16A34A', fontWeight: 700, fontSize: 14 }}>
                                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: data.segment_couleur || '#16A34A', marginRight: 6, verticalAlign: 'middle' }} />
                                        {data.segment_nom}
                                    </div>
                                </div>
                            )}
                            {data.commercial_nom && (
                                <div style={{
                                    background: '#EFF6FF',
                                    border: '1px solid #BFDBFE',
                                    borderRadius: 12, padding: '8px 16px', textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Commercial</div>
                                    <div style={{ color: '#3B82F6', fontWeight: 700, fontSize: 14 }}>
                                        {data.commercial_nom}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mini stats bar */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginTop: 18,
                        background: '#F8FAFC', borderRadius: 12, padding: '14px 20px', border: '1px solid #F1F5F9'
                    }}>
                        {[
                            { label: 'CA Total', value: fmt(sg.ca_total) + ' F', icon: <FiDollarSign size={14} /> },
                            { label: 'Activites', value: String(n(sg.total_activites)), icon: <FiActivity size={14} /> },
                            { label: 'Visites', value: String(n(sg.total_visites)), icon: <FiEye size={14} /> },
                            { label: 'Ventes', value: String(n(sg.total_ventes)), icon: <FiShoppingBag size={14} /> },
                            { label: 'Note', value: noteMoyenne.moyenne ? Number(noteMoyenne.moyenne).toFixed(1) + '/5' : '-', icon: <FiStar size={14} /> },
                        ].map((s, i) => (
                            <div key={i} style={{ textAlign: 'center' }}>
                                <div style={{ color: '#94A3B8', fontSize: 11, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{s.icon} {s.label}</div>
                                <div style={{ color: '#1E293B', fontWeight: 700, fontSize: 16 }}>{s.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* =================== KPI CARDS =================== */}
            <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 22 }}>
                {[
                    { icon: <FiDollarSign size={22} />, color: '#16A34A', bg: '#DCFCE7', value: fmt(sm.ca) + ' F', label: 'CA du mois', growth: caGrowth },
                    { icon: <FiActivity size={22} />, color: '#3B82F6', bg: '#EFF6FF', value: String(n(sm.nb_activites)), label: 'Activites du mois', growth: actGrowth },
                    { icon: <FiEye size={22} />, color: '#14B8A6', bg: '#CCFBF1', value: String(n(sm.nb_visites)), label: 'Visites du mois', growth: null },
                    { icon: <FiShoppingBag size={22} />, color: '#8B5CF6', bg: '#F3E8FF', value: String(n(sm.nb_ventes)), label: 'Ventes du mois', growth: null },
                    { icon: <FiPackage size={22} />, color: '#F59E0B', bg: '#FEF3C7', value: String(n(sg.total_commandes)), label: 'Commandes totales', growth: null },
                ].map((kpi, i) => (
                    <div key={i} className="kpi-card" style={{
                        background: '#fff', borderRadius: 14, padding: '20px 18px', border: '1px solid #E2E8F0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden',
                        transition: 'box-shadow 0.2s, transform 0.2s', cursor: 'default'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: kpi.color, borderRadius: '14px 0 0 14px' }} />
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                            <div style={{ width: 46, height: 46, borderRadius: 12, background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, flexShrink: 0 }}>
                                {kpi.icon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{kpi.label}</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#1E293B', letterSpacing: -0.3 }}>{kpi.value}</div>
                                {kpi.growth !== null && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                            color: kpi.growth >= 0 ? '#16A34A' : '#EF4444',
                                            background: kpi.growth >= 0 ? '#DCFCE7' : '#FEE2E2',
                                            display: 'inline-flex', alignItems: 'center', gap: 2
                                        }}>
                                            {kpi.growth >= 0 ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
                                            {kpi.growth >= 0 ? '+' : ''}{kpi.growth}%
                                        </span>
                                        <span style={{ fontSize: 11, color: '#94A3B8' }}>vs mois prec.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* =================== TABS =================== */}
            <div style={{
                display: 'flex', gap: 2, marginBottom: 22, background: '#fff', border: '1px solid #E2E8F0',
                padding: 4, borderRadius: 12, width: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
                {[
                    { key: 'resume', label: 'Resume', icon: <FiBarChart2 size={14} /> },
                    { key: 'activites', label: `Activites (${activites.length})`, icon: <FiList size={14} /> },
                    { key: 'interactions', label: `Interactions (${interactions.length})`, icon: <FiMessageSquare size={14} /> },
                    { key: 'feedbacks', label: `Feedbacks (${feedbacks.length})`, icon: <FiStar size={14} /> }
                ].map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key as any)}
                        style={{
                            padding: '9px 20px', border: 'none', borderRadius: 9, cursor: 'pointer',
                            fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                            background: activeTab === t.key ? '#16A34A' : 'transparent',
                            color: activeTab === t.key ? '#fff' : '#64748B',
                            transition: 'all 0.2s', boxShadow: activeTab === t.key ? '0 2px 8px rgba(22,163,74,0.3)' : 'none'
                        }}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* =================== TAB: RESUME =================== */}
            {activeTab === 'resume' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Row 1: Evolution + Note + Repartition */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 1fr', gap: 18 }}>
                        {/* Evolution Chart */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiBarChart2 style={{ color: '#3B82F6' }} /> Evolution 6 mois
                            </h3>
                            {evolution.length > 0 ? (
                                <Chart type="area" height={260} options={{
                                    chart: { toolbar: { show: false }, background: 'transparent', fontFamily: 'DM Sans, sans-serif' },
                                    xaxis: {
                                        categories: evolution.map((e: any) => { const [y, m] = e.mois.split('-'); return MOIS[parseInt(m) - 1]; }),
                                        labels: { style: { colors: '#94A3B8', fontSize: '12px' } },
                                        axisBorder: { show: false }, axisTicks: { show: false }
                                    },
                                    yaxis: [
                                        { title: { text: '' }, labels: { style: { colors: '#94A3B8', fontSize: '11px' }, formatter: (v: number) => String(Math.round(v)) } },
                                        { opposite: true, title: { text: '' }, labels: { style: { colors: '#94A3B8', fontSize: '11px' }, formatter: (v: number) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : String(v) } }
                                    ],
                                    stroke: { curve: 'smooth', width: [2, 3] },
                                    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] } },
                                    colors: ['#3B82F6', '#16A34A'],
                                    grid: { borderColor: '#F1F5F9', strokeDashArray: 4 },
                                    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px', fontWeight: 600, markers: { size: 6, shape: 'circle' as const } },
                                    tooltip: { y: { formatter: (v: number, { seriesIndex }: any) => seriesIndex === 1 ? fmt(v) + ' F' : String(v) } }
                                }} series={[
                                    { name: 'Activites', data: evolution.map((e: any) => n(e.activites)) },
                                    { name: 'CA (FCFA)', data: evolution.map((e: any) => n(e.ca)) }
                                ]} />
                            ) : <EmptyState text="Pas assez de donnees" />}
                        </div>

                        {/* Note moyenne + satisfaction */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, color: '#1E293B', textAlign: 'center' }}>Satisfaction Client</h3>
                            {noteMoyenne.total > 0 ? (
                                <>
                                    <div style={{ position: 'relative', width: 130, height: 130 }}>
                                        <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                            <circle cx="50" cy="50" r="42" fill="none" stroke="#F1F5F9" strokeWidth="10" />
                                            <circle cx="50" cy="50" r="42" fill="none"
                                                stroke={Number(noteMoyenne.moyenne) >= 4 ? '#16A34A' : Number(noteMoyenne.moyenne) >= 3 ? '#F59E0B' : '#EF4444'}
                                                strokeWidth="10"
                                                strokeDasharray={`${(Number(noteMoyenne.moyenne) / 5) * 264} ${264 - (Number(noteMoyenne.moyenne) / 5) * 264}`}
                                                strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
                                        </svg>
                                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                            <span style={{ fontSize: 30, fontWeight: 800, color: Number(noteMoyenne.moyenne) >= 4 ? '#16A34A' : Number(noteMoyenne.moyenne) >= 3 ? '#F59E0B' : '#EF4444', letterSpacing: -1 }}>
                                                {Number(noteMoyenne.moyenne).toFixed(1)}
                                            </span>
                                            <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>/ 5</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 2, marginTop: 14 }}>
                                        {[1, 2, 3, 4, 5].map(s => (
                                            <span key={s} style={{ fontSize: 20, color: s <= Math.round(Number(noteMoyenne.moyenne)) ? '#F59E0B' : '#E2E8F0' }}>★</span>
                                        ))}
                                    </div>
                                    <span style={{ fontSize: 12, color: '#94A3B8', marginTop: 8 }}>{n(noteMoyenne.total)} avis</span>
                                </>
                            ) : <EmptyState text="Aucun feedback" />}

                            {/* Quick stats */}
                            <div style={{ width: '100%', marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { label: 'CA Mois', value: fmt(sm.ca) + ' F', pct: caGrowth },
                                    { label: 'Activites', value: String(n(sm.nb_activites)), pct: actGrowth },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                        <span style={{ color: '#64748B', fontWeight: 500 }}>{item.label}</span>
                                        <span style={{
                                            fontWeight: 700, fontSize: 11, padding: '1px 8px', borderRadius: 10,
                                            color: item.pct >= 0 ? '#16A34A' : '#EF4444',
                                            background: item.pct >= 0 ? '#DCFCE7' : '#FEE2E2'
                                        }}>
                                            {item.pct >= 0 ? '+' : ''}{item.pct}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Repartition par type */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiPieChart style={{ color: '#8B5CF6' }} /> Par type
                            </h3>
                            {parType.length > 0 ? (
                                <>
                                    <Chart type="donut" height={200} options={{
                                        labels: parType.map((t: any) => t.type),
                                        colors: parType.map((t: any) => TYPE_COLORS[t.type] || '#94A3B8'),
                                        legend: { show: false },
                                        dataLabels: { enabled: false },
                                        plotOptions: { pie: { donut: { size: '65%', labels: { show: true, total: { show: true, label: 'Total', fontSize: '13px', fontWeight: '700', color: '#1E293B' }, value: { fontSize: '20px', fontWeight: '800', color: '#1E293B' } } } } },
                                        stroke: { width: 2, colors: ['#fff'] }
                                    }} series={parType.map((t: any) => n(t.total))} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                        {parType.map((t: any, i: number) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLORS[t.type] || '#94A3B8' }} />
                                                    <span style={{ color: '#475569', fontWeight: 500, textTransform: 'capitalize' }}>{t.type}</span>
                                                </div>
                                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                    <span style={{ fontWeight: 700, color: '#1E293B' }}>{n(t.total)}</span>
                                                    {n(t.ca) > 0 && <span style={{ fontSize: 11, color: '#94A3B8' }}>{fmt(t.ca)} F</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : <EmptyState text="Aucune activite" />}
                        </div>
                    </div>

                    {/* Row 2: Informations + Top Commerciaux + Derniere activite */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
                        {/* Informations */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Informations</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { icon: <FiUser size={14} />, label: 'Contact', value: `${data.prenom_contact || ''} ${data.nom_contact || ''}`.trim() || '-' },
                                    { icon: <FiPhone size={14} />, label: 'Telephone', value: data.telephone || '-' },
                                    { icon: <FiMail size={14} />, label: 'Email', value: data.email || '-' },
                                    { icon: <FiMapPin size={14} />, label: 'Adresse', value: data.adresse ? `${data.adresse}, ${data.ville || ''}` : data.ville || '-' },
                                    { icon: <FiGlobe size={14} />, label: 'Pays', value: data.pays || 'Senegal' },
                                    { icon: <FiNavigation size={14} />, label: 'Zone', value: data.zone_nom || '-' },
                                    { icon: <FiUsers size={14} />, label: 'Commercial', value: data.commercial_nom || '-' },
                                    { icon: <FiCalendar size={14} />, label: 'Client depuis', value: data.created_at ? new Date(data.created_at).toLocaleDateString('fr-FR') : '-' },
                                ].map((info, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                                        background: '#F8FAF9', borderRadius: 10, border: '1px solid #F1F5F9'
                                    }}>
                                        <div style={{ color: '#16A34A', flexShrink: 0 }}>{info.icon}</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{info.label}</div>
                                            <div style={{ fontSize: 13, color: '#1E293B', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{info.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {data.notes && (
                                <div style={{ marginTop: 12, padding: '12px 14px', background: '#FFFBEB', borderRadius: 10, border: '1px solid #FDE68A', fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
                                    <strong>Notes:</strong> {data.notes}
                                </div>
                            )}
                        </div>

                        {/* Top Commerciaux */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiUsers style={{ color: '#3B82F6' }} /> Top Commerciaux
                            </h3>
                            {topCommerciaux.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {topCommerciaux.map((c: any, i: number) => (
                                        <div key={c.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                                            background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9',
                                            cursor: 'pointer', transition: 'all 0.2s'
                                        }}
                                        onClick={() => navigate(`/admin/commerciaux/${c.id}`)}
                                        onMouseEnter={e => { e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.borderColor = '#BFDBFE'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = '#F1F5F9'; }}>
                                            <div style={{
                                                width: 28, height: 28, borderRadius: 8, background: i === 0 ? '#FEF3C7' : i === 1 ? '#F1F5F9' : i === 2 ? '#FED7AA' : '#F8FAFC',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                                                color: i === 0 ? '#D97706' : i === 1 ? '#64748B' : i === 2 ? '#EA580C' : '#94A3B8'
                                            }}>
                                                {i + 1}
                                            </div>
                                            {c.photo_url ? (
                                                <img src={`${API_URL}${c.photo_url}`} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#16A34A15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#16A34A' }}>
                                                    {(c.nom || '?').substring(0, 2).toUpperCase()}
                                                </div>
                                            )}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nom}</div>
                                                <div style={{ fontSize: 11, color: '#94A3B8' }}>{n(c.nb_visites)} visites</div>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>{fmt(c.ca)} F</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <EmptyState text="Aucun commercial" />}
                        </div>

                        {/* Activites recentes */}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiActivity style={{ color: '#14B8A6' }} /> Activites recentes
                            </h3>
                            {activites.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
                                    {activites.slice(0, 10).map((a: any) => (
                                        <div key={a.id} style={{
                                            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                                            background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9'
                                        }}>
                                            <div style={{
                                                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                                background: (TYPE_COLORS[a.type] || '#94A3B8') + '15',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                                            }}>
                                                {TYPE_ICONS[a.type] || '📌'}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{
                                                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                                        color: TYPE_COLORS[a.type] || '#64748B',
                                                        background: (TYPE_COLORS[a.type] || '#94A3B8') + '15',
                                                        textTransform: 'capitalize'
                                                    }}>{a.type}</span>
                                                    {n(a.montant) > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>{fmt(a.montant)} F</span>}
                                                </div>
                                                {a.description && <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.4 }}>{a.description}</div>}
                                                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                                                    {new Date(a.horodatage).toLocaleDateString('fr-FR')} - {a.user_nom}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <EmptyState text="Aucune activite" />}
                        </div>
                    </div>
                </div>
            )}

            {/* =================== TAB: ACTIVITES =================== */}
            {activeTab === 'activites' && (
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div style={{ padding: '18px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Toutes les activites ({activites.length})</h3>
                    </div>
                    {activites.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#F8FAFC' }}>
                                    {['Type', 'Description', 'Montant', 'Quantite', 'Date', 'Commercial', 'Statut'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', borderBottom: '1px solid #F1F5F9' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {activites.map((a: any) => (
                                    <tr key={a.id} style={{ borderBottom: '1px solid #F8FAFC', transition: 'background 0.15s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '12px 14px' }}>
                                            <span style={{
                                                fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
                                                color: TYPE_COLORS[a.type] || '#64748B',
                                                background: (TYPE_COLORS[a.type] || '#94A3B8') + '15',
                                                textTransform: 'capitalize', whiteSpace: 'nowrap'
                                            }}>
                                                {TYPE_ICONS[a.type] || '📌'} {a.type}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 14px', fontSize: 13, color: '#475569', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description || '-'}</td>
                                        <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: n(a.montant) > 0 ? 700 : 400, color: n(a.montant) > 0 ? '#16A34A' : '#94A3B8' }}>
                                            {n(a.montant) > 0 ? fmt(a.montant) + ' F' : '-'}
                                        </td>
                                        <td style={{ padding: '12px 14px', fontSize: 13, color: '#475569' }}>{a.quantite || '-'}</td>
                                        <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>{new Date(a.horodatage).toLocaleDateString('fr-FR')}</td>
                                        <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748B' }}>{a.user_nom}</td>
                                        <td style={{ padding: '12px 14px' }}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                                                color: a.statut === 'valide' ? '#16A34A' : a.statut === 'annule' ? '#EF4444' : '#F59E0B',
                                                background: a.statut === 'valide' ? '#DCFCE7' : a.statut === 'annule' ? '#FEE2E2' : '#FEF3C7'
                                            }}>{a.statut}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <EmptyState text="Aucune activite enregistree" />}
                </div>
            )}

            {/* =================== TAB: INTERACTIONS =================== */}
            {activeTab === 'interactions' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                    {/* Add form */}
                    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FiMessageSquare style={{ color: '#3B82F6' }} /> Ajouter une interaction
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <select value={newInteraction.type} onChange={e => setNewInteraction({ ...newInteraction, type: e.target.value })}
                                style={{ padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 13, color: '#1E293B', background: '#F8FAFC', outline: 'none' }}>
                                <option value="note">Note</option><option value="appel">Appel</option><option value="email">Email</option><option value="visite">Visite</option><option value="reclamation">Reclamation</option>
                            </select>
                            <textarea value={newInteraction.contenu} onChange={e => setNewInteraction({ ...newInteraction, contenu: e.target.value })}
                                placeholder="Contenu de l'interaction..."
                                rows={4}
                                style={{ padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 13, color: '#1E293B', background: '#F8FAFC', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                            <button onClick={handleAddInteraction}
                                style={{ padding: '10px 20px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'background 0.2s', alignSelf: 'flex-start' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#15803D'}
                                onMouseLeave={e => e.currentTarget.style.background = '#16A34A'}>
                                Ajouter
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', maxHeight: 500, overflowY: 'auto' }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Historique ({interactions.length})</h3>
                        {interactions.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {interactions.map((i: any) => (
                                    <div key={i.id} style={{
                                        padding: '12px 14px', background: '#F8FAFC', borderRadius: 10,
                                        border: '1px solid #F1F5F9', borderLeft: '3px solid #3B82F6'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                                color: '#3B82F6', background: '#EFF6FF', textTransform: 'capitalize'
                                            }}>{i.type}</span>
                                            <span style={{ fontSize: 11, color: '#94A3B8' }}>{new Date(i.created_at).toLocaleDateString('fr-FR')}</span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{i.contenu}</p>
                                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>Par {i.user_nom}</div>
                                    </div>
                                ))}
                            </div>
                        ) : <EmptyState text="Aucune interaction" />}
                    </div>
                </div>
            )}

            {/* =================== TAB: FEEDBACKS =================== */}
            {activeTab === 'feedbacks' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                    {/* Add form */}
                    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FiStar style={{ color: '#F59E0B' }} /> Ajouter un feedback
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                                <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6, fontWeight: 500 }}>Note</div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    {[1, 2, 3, 4, 5].map(s => (
                                        <span key={s} onClick={() => setNewFeedback({ ...newFeedback, note: s })}
                                            style={{ fontSize: 28, cursor: 'pointer', color: s <= newFeedback.note ? '#F59E0B' : '#E2E8F0', transition: 'transform 0.15s' }}
                                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                                            ★
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <textarea value={newFeedback.commentaire} onChange={e => setNewFeedback({ ...newFeedback, commentaire: e.target.value })}
                                placeholder="Commentaire..."
                                rows={4}
                                style={{ padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 13, color: '#1E293B', background: '#F8FAFC', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                            <button onClick={handleAddFeedback}
                                style={{ padding: '10px 20px', background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'background 0.2s', alignSelf: 'flex-start' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#D97706'}
                                onMouseLeave={e => e.currentTarget.style.background = '#F59E0B'}>
                                Ajouter
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', maxHeight: 500, overflowY: 'auto' }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Tous les feedbacks ({feedbacks.length})</h3>
                        {feedbacks.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {feedbacks.map((f: any) => (
                                    <div key={f.id} style={{
                                        padding: '12px 14px', background: '#F8FAFC', borderRadius: 10,
                                        border: '1px solid #F1F5F9', borderLeft: '3px solid #F59E0B'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                            <div style={{ display: 'flex', gap: 2 }}>
                                                {[1, 2, 3, 4, 5].map(s => (
                                                    <span key={s} style={{ fontSize: 14, color: s <= f.note ? '#F59E0B' : '#E2E8F0' }}>★</span>
                                                ))}
                                            </div>
                                            <span style={{ fontSize: 11, color: '#94A3B8' }}>{new Date(f.created_at).toLocaleDateString('fr-FR')}</span>
                                        </div>
                                        {f.commentaire && <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{f.commentaire}</p>}
                                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>Par {f.user_nom}</div>
                                    </div>
                                ))}
                            </div>
                        ) : <EmptyState text="Aucun feedback" />}
                    </div>
                </div>
            )}

            {/* =================== QR Code Modal =================== */}
            {showQRModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 400, maxWidth: '90%', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>QR Code Client</h3>
                            <button onClick={() => setShowQRModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748B' }}><FiX size={22} /></button>
                        </div>
                        <div ref={qrRef} style={{ padding: 24, background: '#F8FAFC', borderRadius: 16, display: 'inline-block', marginBottom: 16 }}>
                            <QRCodeSVG value={qrValue} size={220} level="H" includeMargin bgColor="#F8FAFC" fgColor="#1E293B" />
                        </div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{data.raison_sociale}</h3>
                        <p style={{ color: '#64748B', fontSize: 14, marginBottom: 4 }}>{qrValue}</p>
                        <p style={{ color: '#94A3B8', fontSize: 13, marginBottom: 20 }}>{data.nom_contact} {data.prenom_contact} - {data.telephone}</p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                            <button onClick={downloadQR} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                <FiDownload size={16} /> Telecharger
                            </button>
                            <button onClick={printQR} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', border: 'none', borderRadius: 10, background: '#1E293B', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                <FiPrinter size={16} /> Imprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientDetail;
